import { useState, useEffect } from 'react';

/**
 * Hook to detect if app is running in Telegram Web App and get initData
 * @returns {Object} { isTelegramWebApp: boolean, initData?: string }
 */
export const useTelegramWebApp = () => {
    const [isTelegramWebApp, setIsTelegramWebApp] = useState(false);
    const [initData, setInitData] = useState(null);

    useEffect(() => {
        // Check if Telegram WebApp SDK is available
        if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
            const webApp = window.Telegram.WebApp;
            
            // Force light theme BEFORE ready() - this is critical
            try {
                webApp.setOptions({ 
                    colorScheme: 'light',
                    themeParams: {
                        bg_color: '#ffffff',
                        text_color: '#000000',
                        hint_color: '#999999',
                        link_color: '#2481cc',
                        button_color: '#2481cc',
                        button_text_color: '#ffffff'
                    }
                });
            } catch {}
            
            // Wait for WebApp to be ready before getting initData
            // Telegram Web App needs to be ready before initData is available
            webApp.ready();
            
            // Force light theme again after ready() to ensure it sticks
            try {
                webApp.setOptions({ colorScheme: 'light' });
            } catch {}
            
            webApp.expand(); // Expand to full screen
            
            // Get initData from Telegram Web App
            // initData is a string (URL-encoded), initDataUnsafe is an object (parsed)
            // We need initData (string) for backend validation
            const getInitDataString = () => {
                const initData = webApp.initData;
                // Ensure it's a string before processing
                if (typeof initData === 'string' && initData.trim() !== '') {
                    return initData;
                }
                return null;
            };
            
            // Try to get initData immediately
            let telegramInitData = getInitDataString();
            
            if (telegramInitData) {
                setInitData(telegramInitData);
                setIsTelegramWebApp(true); // We are really inside Telegram WebApp (have signed initData)
            } else {
                // Try multiple times with increasing delays (SDK might need time to initialize)
                let retryCount = 0;
                const maxRetries = 20; // Try up to 20 times (5 seconds total)
                
                // Use a recursive timeout approach instead of setInterval for better control
                const tryGetInitData = () => {
                    retryCount++;
                    telegramInitData = getInitDataString();
                    
                    if (telegramInitData) {
                        setInitData(telegramInitData);
                        setIsTelegramWebApp(true); // We are really inside Telegram WebApp (have signed initData)
                        return; // Stop retrying
                    }
                    
                    if (retryCount < maxRetries) {
                        // Progressive delay: 100ms, 200ms, then 300ms for remaining retries
                        const delay = retryCount <= 1 ? 100 : (retryCount <= 2 ? 200 : 300);
                        setTimeout(tryGetInitData, delay);
                    }
                };
                
                // Start retrying after initial delay
                setTimeout(tryGetInitData, 100);
            }
        }
    }, []);

    return { isTelegramWebApp, initData };
};

